package ai.jessie.web.settings

import ai.jessie.shared.settings.SettingsFieldProblem

internal val settingLabels =
    linkedMapOf(
        "name" to "Rechtlicher Name / Handelsname (1–200 Zeichen)",
        "timeZone" to "Zeitzone (IANA, z. B. Europe/Zurich)",
        "address" to "Postadresse (1–500 Zeichen)",
        "countryCode" to "Land (ISO-Code, z. B. CH)",
        "iban" to "IBAN (optional)",
        "vatNumber" to "MWST-Nummer (optional, höchstens 50 Zeichen)",
        "invoicePrefix" to "Rechnungspräfix (1–20 Zeichen)",
        "nextInvoiceNumber" to "Nächste Rechnungsnummer",
        "defaultCurrency" to "Standardwährung",
        "invoiceLanguage" to "Rechnungssprache (BCP 47, z. B. de-CH)",
        "paymentTermsDays" to "Zahlungsfrist in Tagen (0–365)",
        "automaticDocumentExtraction" to "Automatische Dokumentextraktion",
    )

// Read-only views need the name without the input hint in brackets.
internal fun viewLabel(field: String): String = settingLabels[field]?.substringBefore(" (") ?: field

// Request fields (`vatRates[0].rate`), history fields (`vatRate[<id>].rate`,
// `invoiceSequence[RE].nextNumber`) and scalar names alike. Internal IDs are not shown.
internal fun fieldLabel(field: String): String {
    val match = ROW_FIELD.matchEntire(field) ?: return rowKinds[field] ?: viewLabel(field)
    val (kind, key, part) = match.destructured
    val row =
        when {
            key.toIntOrNull() != null -> " ${key.toInt() + 1}"
            kind == "invoiceSequence" -> " $key"
            key.startsWith("neu ") -> " (neu)"
            else -> ""
        }
    return "${rowKinds[kind] ?: kind}$row: ${rowParts[part] ?: part}"
}

private val ROW_FIELD = Regex("""(\w+)\[([^\]]*)\]\.(\w+)""")

private val rowKinds =
    mapOf(
        "vatRates" to "MWST-Version",
        "vatRate" to "MWST-Version",
        "documentCategories" to "Dokumentkategorie",
        "documentCategory" to "Dokumentkategorie",
        "documentFolders" to "Dokumentordner",
        "documentFolder" to "Dokumentordner",
        "invoiceSequence" to "Nummernkreis",
    )

private val rowParts =
    mapOf(
        "name" to "Name",
        "rate" to "Satz",
        "effectiveFrom" to "gültig ab",
        "effectiveTo" to "gültig bis",
        "remove" to "entfernen",
        "active" to "aktiv",
        "id" to "Eintrag",
        "vatRateId" to "Version",
        "nextNumber" to "nächste Nummer",
    )

internal fun SettingsFieldProblem.correction(): String =
    when (reason) {
        "REQUIRED" -> "Bitte einen Wert eingeben."
        "TOO_LONG" -> "Bitte die angegebene maximale Länge einhalten."
        "INVALID" -> "Bitte das angegebene Format verwenden."
        "UNSUPPORTED" -> "Bitte CHF, EUR oder USD wählen."
        "OUT_OF_RANGE" -> "Bitte den angegebenen Wertebereich einhalten."
        "BELOW_MINIMUM" -> "Die nächste Nummer muss mindestens $minimum sein."
        "AMBIGUOUS" ->
            "Dieses Präfix kann mit einem bisherigen Präfix gleiche Rechnungsnummern ergeben."
        "BACKDATED" ->
            "Die neue MWST-Version muss heute oder später beginnen (Organisationszeitzone)."
        "OVERLAP" -> "MWST-Zeiträume desselben Namens dürfen sich nicht überschneiden."
        "IMMUTABLE" -> "Diese Version kann nicht geändert werden. Bitte eine neue Version anlegen."
        "DUPLICATE" ->
            "Bitte einen eindeutigen Namen verwenden; archivierte Einträge zählen ebenfalls."
        "OPEN_TIMER" ->
            "Zuerst laufende oder noch abzuschliessende Timer schliessen. Dein Vorschlag bleibt erhalten."
        else -> "Bitte dieses Feld prüfen."
    }
